import Accounting from "accounting";

/**
 * Matches backend ReportConfigurationDto.ReportConfPlan.
 *
 * @typedef {object} ReportConfPlan
 * @property {number} id
 * @property {string} name
 * @property {string} [billingPeriod]
 * @property {number} [cost]
 * @property {string} [policy]
 */

/**
 * Matches backend ReportConfigurationDto.ReportConfPlanGroup.
 *
 * @typedef {object} ReportConfPlanGroup
 * @property {number} id
 * @property {string} [name]
 */

/**
 * Matches backend ReportConfigurationDto (GET api/v1/report-configurations).
 *
 * @typedef {object} ReportConfigurationDto
 * @property {number} id
 * @property {string} name
 * @property {boolean} active
 * @property {number} businessId
 * @property {number} segmentId
 * @property {string} [segmentName]
 * @property {number} [sheetTagGroupId]
 * @property {string} [sheetGroupName]
 * @property {number} [sectionTagGroupId]
 * @property {string} [sectionGroupName]
 * @property {ReportConfPlan[]} [columnPlans]
 * @property {ReportConfPlanGroup[]} [columnOwnerPlanGroups]
 * @property {ReportConfPlanGroup[]} [columnContributionPlanGroups]
 */

const toPlain = (data) => {
  if (data == null) return data;
  if (typeof data.asMutable === "function") return data.asMutable({ deep: true });
  if (typeof data.toJS === "function") return data.toJS();
  return data;
};

/**
 * Normalizes GET report-configurations response to ReportConfigurationDto[].
 * @param {*} data
 * @returns {ReportConfigurationDto[]}
 */
export const normalizeReportConfigurations = (data) => {
  const plain = toPlain(data);
  if (!plain) return [];
  if (Array.isArray(plain)) return plain;
  if (Array.isArray(plain.content)) return plain.content;
  if (Array.isArray(plain.reportConfigurations)) return plain.reportConfigurations;
  return [];
};

const toIterableArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [...value];
};

const toColumnPlansArray = (columnPlans) => toIterableArray(columnPlans);

const buildIdRefs = (rawIds) => {
  const seen = new Set();
  const refs = [];
  toIterableArray(rawIds).forEach((item) => {
    const id = Number(typeof item === "object" && item != null ? item.id : item);
    if (Number.isNaN(id) || seen.has(id)) return;
    seen.add(id);
    refs.push({ id });
  });
  return refs;
};

/**
 * @param {ReportConfPlanGroup} group
 * @returns {string}
 */
export const formatReportConfPlanGroupLabel = (group) =>
  group?.name?.trim() || (group?.id != null ? `Group ${group.id}` : "");

/**
 * @param {object} form
 * @param {number[]} [form.planIds]
 * @param {number[]} [form.ownerPlanGroupIds]
 * @param {number[]} [form.contributionPlanGroupIds]
 * @returns {boolean}
 */
export const hasAnyReportColumns = ({
  planIds,
  ownerPlanGroupIds,
  contributionPlanGroupIds,
}) =>
  !!(
    planIds?.length ||
    ownerPlanGroupIds?.length ||
    contributionPlanGroupIds?.length
  );

/**
 * @param {number[]} [ownerPlanGroupIds]
 * @param {number[]} [contributionPlanGroupIds]
 * @returns {boolean}
 */
export const ownerContributionPlanGroupsOverlap = (
  ownerPlanGroupIds,
  contributionPlanGroupIds
) => {
  const ownerIds = new Set((ownerPlanGroupIds || []).map(String));
  return (contributionPlanGroupIds || []).some((id) => ownerIds.has(String(id)));
};

/**
 * @param {number|string} cost
 * @param {string} [currency] - business country currency (e.g. GHS)
 * @returns {string|null}
 */
export const formatReportConfPlanCost = (cost, currency) => {
  if (cost == null || cost === "") return null;
  const amount = Number(cost);
  if (Number.isNaN(amount)) return null;
  if (!currency) {
    return Accounting.formatMoney(amount, { precision: 2 });
  }
  return Accounting.formatMoney(amount, {
    symbol: currency,
    format: "%s %v",
    thousand: ",",
    decimal: ".",
    precision: 2,
  });
};

/**
 * @param {ReportConfPlan} plan
 * @param {string} [currency] - fallback when plan has no embedded business currency
 * @returns {string}
 */
export const formatReportConfPlanLabel = (plan, currency) => {
  const parts = [];
  if (plan?.name) parts.push(plan.name);
  if (plan?.billingPeriod) parts.push(plan.billingPeriod);
  const planCurrency =
    plan?.business?.currency || plan?.currency || currency;
  const costLabel = formatReportConfPlanCost(plan?.cost, planCurrency);
  if (costLabel) parts.push(costLabel);
  return parts.join(" - ");
};

/**
 * @param {ReportConfPlan[]|Set<ReportConfPlan>|*} columnPlans
 * @returns {string}
 */
export const formatReportConfigurationColumnPlans = (columnPlans, currency) =>
  getReportConfigurationColumnPlanLabels(columnPlans, currency)
    .map((item) => item.label)
    .join(", ");

/**
 * @param {ReportConfPlan[]|Set<ReportConfPlan>|*} columnPlans
 * @param {string} [currency]
 * @returns {{ id: number, label: string }[]}
 */
export const getReportConfigurationColumnPlanLabels = (columnPlans, currency) =>
  toColumnPlansArray(columnPlans)
    .map((plan) => ({
      id: plan.id,
      label: formatReportConfPlanLabel(plan, currency),
    }))
    .filter((item) => item.label);

/**
 * @param {ReportConfigurationDto|object} config
 * @param {string} [currency]
 * @returns {Array<{ key: string, titleKey: string, items: { id: number, label: string }[] }>}
 */
export const getReportConfigurationColumnSections = (config, currency) => {
  const plain = toPlain(config) || config;
  const sections = [];

  const plans = getReportConfigurationColumnPlanLabels(plain.columnPlans, currency);
  if (plans.length) {
    sections.push({
      key: "plans",
      titleKey: "reportConfigs.column_plans",
      items: plans,
    });
  }

  const ownerGroups = toIterableArray(plain.columnOwnerPlanGroups)
    .map((group) => ({
      id: group.id,
      label: formatReportConfPlanGroupLabel(group),
    }))
    .filter((item) => item.label);
  if (ownerGroups.length) {
    sections.push({
      key: "owner",
      titleKey: "reportConfigs.column_owner_plan_groups",
      items: ownerGroups,
    });
  }

  const contributionGroups = toIterableArray(plain.columnContributionPlanGroups)
    .map((group) => ({
      id: group.id,
      label: formatReportConfPlanGroupLabel(group),
    }))
    .filter((item) => item.label);
  if (contributionGroups.length) {
    sections.push({
      key: "contribution",
      titleKey: "reportConfigs.column_contribution_plan_groups",
      items: contributionGroups,
    });
  }

  return sections;
};

/**
 * Maps API/Redux report configurations to plain rows for the list table.
 * @param {*} data
 * @returns {Array<object>}
 */
export const mapReportConfigurationsForTable = (data) => {
  const list = normalizeReportConfigurations(data);
  return list.map((config) => {
    const plain = toPlain(config) || config;
    return {
      id: plain.id,
      name: plain.name ?? "—",
      segmentName: plain.segmentName ?? "—",
      sheetGroupName: plain.sheetGroupName ?? "—",
      sectionGroupName: plain.sectionGroupName ?? "—",
      columnPlans: toColumnPlansArray(plain.columnPlans),
      columnOwnerPlanGroups: toIterableArray(plain.columnOwnerPlanGroups),
      columnContributionPlanGroups: toIterableArray(
        plain.columnContributionPlanGroups
      ),
      active: plain.active,
    };
  });
};

/**
 * @param {ReportConfigurationDto} config
 * @returns {number[]}
 */
export const getReportConfigurationPlanIds = (config) =>
  toColumnPlansArray(config?.columnPlans)
    .map((plan) => plan.id)
    .filter((id) => id != null);

/**
 * @param {ReportConfigurationDto[]} configs
 * @returns {ReportConfigurationDto[]}
 */
export const getActiveReportConfigurations = (configs) =>
  (configs || []).filter((config) => config.active !== false);

export const tagGroupsConflict = (sheetTagGroupId, sectionTagGroupId) =>
  !!sheetTagGroupId &&
  !!sectionTagGroupId &&
  String(sheetTagGroupId) === String(sectionTagGroupId);

/**
 * Matches backend ReportConfigurationCreateRequest (POST/PUT api/v1/report-configurations).
 *
 * @typedef {object} ReportConfigurationCreateRequest
 * @property {number} businessId
 * @property {number} segmentId
 * @property {number} [sheetTagGroupId]
 * @property {number} [sectionTagGroupId]
 * @property {string} name
 * @property {boolean} [active]
 * @property {{ id: number }[]} [columnPlans]
 * @property {{ id: number }[]} [columnOwnerPlanGroups]
 * @property {{ id: number }[]} [columnContributionPlanGroups]
 */

/**
 * @param {object} options
 * @param {number} options.businessId
 * @param {string} options.name
 * @param {number} options.segmentId
 * @param {number} [options.sheetTagGroupId]
 * @param {number} [options.sectionTagGroupId]
 * @param {{ id: number }[]|number[]} [options.columnPlans]
 * @param {number[]} [options.columnPlanIds]
 * @param {number[]} [options.planIds]
 * @param {{ id: number }[]|number[]} [options.columnOwnerPlanGroups]
 * @param {number[]} [options.ownerPlanGroupIds]
 * @param {{ id: number }[]|number[]} [options.columnContributionPlanGroups]
 * @param {number[]} [options.contributionPlanGroupIds]
 * @param {boolean} [options.active]
 * @returns {ReportConfigurationCreateRequest}
 */
export const buildReportConfigurationRequest = ({
  businessId,
  name,
  segmentId,
  sheetTagGroupId,
  sectionTagGroupId,
  columnPlans,
  columnPlanIds,
  planIds,
  columnOwnerPlanGroups,
  ownerPlanGroupIds,
  columnContributionPlanGroups,
  contributionPlanGroupIds,
  active = true,
}) => {
  const columnPlansPayload = buildIdRefs(
    columnPlans ?? columnPlanIds ?? planIds ?? []
  );
  const columnOwnerPlanGroupsPayload = buildIdRefs(
    columnOwnerPlanGroups ?? ownerPlanGroupIds ?? []
  );
  const columnContributionPlanGroupsPayload = buildIdRefs(
    columnContributionPlanGroups ?? contributionPlanGroupIds ?? []
  );

  const payload = {
    businessId: Number(businessId),
    segmentId: Number(segmentId),
    name: name.trim(),
    active,
  };

  if (sheetTagGroupId != null && sheetTagGroupId !== "") {
    payload.sheetTagGroupId = Number(sheetTagGroupId);
  }
  if (sectionTagGroupId != null && sectionTagGroupId !== "") {
    payload.sectionTagGroupId = Number(sectionTagGroupId);
  }
  if (columnPlansPayload.length) {
    payload.columnPlans = columnPlansPayload;
  }
  if (columnOwnerPlanGroupsPayload.length) {
    payload.columnOwnerPlanGroups = columnOwnerPlanGroupsPayload;
  }
  if (columnContributionPlanGroupsPayload.length) {
    payload.columnContributionPlanGroups = columnContributionPlanGroupsPayload;
  }

  return payload;
};

export const buildCreateReportConfigurationPayload = buildReportConfigurationRequest;
export const buildUpdateReportConfigurationPayload = buildReportConfigurationRequest;

export const REPORT_CONFIGURATION_XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const hasReportConfigurationXlsxPayload = (data) => {
  if (data instanceof ArrayBuffer) return data.byteLength > 0;
  if (data instanceof Blob) return data.size > 0;
  return false;
};

/**
 * Extracts a human-readable error from apisauce/axios binary or JSON responses.
 * @param {*} response
 * @returns {string|null}
 */
export const parseReportGenerateError = (response) => {
  if (response?.problem) {
    return response.problem;
  }

  const { data, status } = response || {};

  if (data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder("utf-8").decode(data);
      if (!text) return status ? `Request failed (${status})` : null;
      try {
        const json = JSON.parse(text);
        return json.message || json.detail || json.title || text;
      } catch {
        return text.slice(0, 200);
      }
    } catch {
      return status ? `Request failed (${status})` : null;
    }
  }

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data && typeof data === "object") {
    return data.message || data.detail || data.title || null;
  }

  return status ? `Request failed (${status})` : null;
};

/**
 * @param {string} [configName]
 * @param {number|string} configId
 * @returns {string}
 */
export const buildReportConfigurationXlsxFilename = (configName, configId) => {
  const safeName = String(configName || "")
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 120);
  return `${safeName || `report-config-${configId}`}.xlsx`;
};

const getResponseHeader = (headers, name) => {
  if (!headers) return null;
  const lower = name.toLowerCase();
  if (typeof headers.get === "function") {
    const value = headers.get(name) || headers.get(lower);
    if (value != null) return value;
  }
  return headers[name] ?? headers[lower] ?? null;
};

/**
 * Parses Content-Disposition from Spring:
 * attachment; filename="name-20260516120000.xlsx"
 *
 * @param {string} disposition
 * @returns {string|null}
 */
export const parseContentDispositionFilename = (disposition) => {
  if (!disposition || typeof disposition !== "string") return null;

  const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;\n]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }

  const quotedMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();

  const unquotedMatch = disposition.match(/filename\s*=\s*([^;\n]+)/i);
  if (unquotedMatch?.[1]) {
    return unquotedMatch[1].trim().replace(/^"|"$/g, "");
  }

  return null;
};

/**
 * Resolves download filename from API response headers.
 * Requires CORS: Access-Control-Expose-Headers: Content-Disposition (or X-Report-Filename).
 *
 * @param {Record<string, string>|import('axios').AxiosResponseHeaders} [headers]
 * @returns {string|null}
 */
export const resolveReportDownloadFilename = (headers) => {
  if (!headers) return null;

  const explicitFilename =
    getResponseHeader(headers, "X-Report-Filename") ||
    getResponseHeader(headers, "X-Filename");
  if (explicitFilename) return String(explicitFilename).trim();

  const disposition = getResponseHeader(headers, "Content-Disposition");
  return parseContentDispositionFilename(disposition);
};

/**
 * @param {ArrayBuffer|Blob} data
 * @param {object} [options]
 * @param {string} [options.filename] - from server Content-Disposition
 * @param {string} [options.configName] - fallback when server omits filename
 * @param {number|string} [options.configId]
 */
export const triggerReportConfigurationXlsxDownload = (
  data,
  { filename, configName, configId } = {}
) => {
  const downloadName =
    filename || buildReportConfigurationXlsxFilename(configName, configId);

  const blob = new Blob([data], { type: REPORT_CONFIGURATION_XLSX_MIME });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", downloadName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
